import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./login.scss";
import {
  TextField,
  IconButton,
  InputAdornment,
  Box,
  Button,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useSlug } from "../../SlugContext";
import { serverUrl } from "../../temp";

const Login = () => {
  const navigate = useNavigate();
  const { setSlugs } = useSlug();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [centerOptions, setCenterOptions] = useState([]);
  const [selectCenterMode, setSelectCenterMode] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const response = await fetch(`${serverUrl}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok || data.status === "false") {
        throw new Error(data.message || "Invalid email or password");
      }

      if (data.status === "multiple") {
        // ✅ Multiple centers → show selection and remember credentials
        setCenterOptions(data.data);
        setSelectCenterMode(true);
        localStorage.setItem("temp_email", email);
        localStorage.setItem("temp_password", password);
        return;
      }

      // ✅ Single center → save data and go home
      const admindata = data.data;
      localStorage.setItem("token", data.token);
      localStorage.setItem("admindata", JSON.stringify(admindata));
      localStorage.setItem("center_id", admindata.center_id);
      localStorage.setItem("center_name", admindata.center_name);
      localStorage.setItem("name", admindata.name);
      localStorage.setItem("email", admindata.email);
      localStorage.setItem("user_id", admindata.id);
      localStorage.setItem("teacher_id", admindata.teacher_ids);
      localStorage.setItem("class_id", admindata.class_ids);
      localStorage.setItem("role_id", admindata.role_id);
      localStorage.setItem("role_names", JSON.stringify(admindata.role_names));
      localStorage.setItem("slugs", JSON.stringify(admindata.slugs));
      setSlugs(admindata.slugs);
      navigate("/home");
    } catch (error) {
      setError(error.message || "Login failed");
    } finally {
      setIsLoading(false);
    }
  };
  const handleCenterSelect = async (center) => {
    try {
      const email = localStorage.getItem("temp_email");
      const password = localStorage.getItem("temp_password");

      // 🔁 Re-login with the same credentials
      const response = await fetch(`${serverUrl}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();
      if (!response.ok || data.status === "false") {
        throw new Error(data.message || "Login failed");
      }

      // ✅ Find selected center in returned data
      const selectedCenter = data.data.find(
        (item) => item.center_id === center.center_id
      );

      if (!selectedCenter)
        throw new Error("Center not found in login response");

      // ✅ Save data to localStorage
      localStorage.setItem("token", data.token || "dummy"); // backend may not give token, optional
      localStorage.setItem("admindata", JSON.stringify(selectedCenter));
      localStorage.setItem("center_id", selectedCenter.center_id);
      localStorage.setItem("center_name", selectedCenter.center_name);
      localStorage.setItem("name", selectedCenter.name);
      localStorage.setItem("email", selectedCenter.email);
      localStorage.setItem("user_id", selectedCenter.id);
      localStorage.setItem("class_id", selectedCenter.class_ids);
      localStorage.setItem("teacher_id", selectedCenter.teacher_ids);
      localStorage.setItem("role_id", selectedCenter.role_id);
      localStorage.setItem(
        "role_names",
        JSON.stringify(selectedCenter.role_names)
      );
      localStorage.setItem("slugs", JSON.stringify(selectedCenter.slugs));
      setSlugs(selectedCenter.slugs);

      // Clean temp credentials
      localStorage.removeItem("temp_email");
      localStorage.removeItem("temp_password");

      navigate("/home");
    } catch (error) {
      setError(error.message || "Center selection failed");
    }
  };

  return (
    <Box className="education-global-login">
      <Box className="login-container">
        <Box className="brand-header">
          <h1>Education Global</h1>
        </Box>

        <Box className="login-card">
          {!selectCenterMode ? (
            <>
              <Box className="welcome-section">
                <h2>Welcome back</h2>
                <p>Please enter your details</p>
              </Box>

              <form onSubmit={handleSubmit}>
                <Box className="form-group">
                  <label>Email address</label>
                  <TextField
                    variant="outlined"
                    fullWidth
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    size="small"
                  />
                </Box>

                <Box className="form-group">
                  <label>Password</label>
                  <TextField
                    variant="outlined"
                    type={showPassword ? "text" : "password"}
                    fullWidth
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    size="small"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            size="small"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                {error && <Box className="error-message">{error}</Box>}

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  className="signin-button"
                  disabled={isLoading}
                >
                  {isLoading ? "Signing in..." : "Sign in"}
                </Button>
              </form>
            </>
          ) : (
            <>
              <Box className="welcome-section">
                <h2>Select Center</h2>
                <p>Your account is linked with multiple centers.</p>
              </Box>

              <Box className="center-selection">
                {centerOptions.map((center, index) => (
                  <Button
                    key={index}
                    variant="outlined"
                    fullWidth
                    size="large"
                    className="center-button"
                    onClick={() => handleCenterSelect(center)}
                  >
                    {center.center_name}
                  </Button>
                ))}
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
